/**
 * Opening an item's evidence file (plan docs/plans/lms-plan.md 12.3, 2.3a
 * "Downloads are presigned URLs"; 18.24's evidence-url routes; WP-47).
 *
 * The 18.27 pattern the certificate download settled: a blank tab is opened
 * inside the click (openPendingTab, the player's helper, so a popup blocker
 * lets it through), then pointed at the presigned URL when the answer arrives;
 * it is closed on failure, and when the popup was blocked a visible link is
 * offered instead (EvidenceBlockedLink).
 */
import { useCallback, useState } from 'react';
import { useToast } from '../../../ui/Toast';
import { openPendingTab } from '../../components/player/DoneStage';
import { describeOnboardingError } from './shared';

export interface EvidenceViewer {
  /** The id of the item whose file is being fetched, or null. */
  opening: string | null;
  /** A URL the popup blocker stopped, keyed by the item, shown as a link. */
  blocked: { id: string; url: string } | null;
  view: (itemId: string, fetchUrl: () => Promise<{ url: string }>) => Promise<void>;
}

export function useEvidenceViewer(): EvidenceViewer {
  const { toast } = useToast();
  const [opening, setOpening] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<{ id: string; url: string } | null>(null);

  const view = useCallback(
    async (itemId: string, fetchUrl: () => Promise<{ url: string }>) => {
      // Before any await: opened inside the click's user activation (18.27).
      const tab = openPendingTab();
      setOpening(itemId);
      setBlocked(null);
      try {
        const { url } = await fetchUrl();
        if (tab && !tab.closed) tab.location.replace(url);
        else setBlocked({ id: itemId, url });
      } catch (err) {
        if (tab && !tab.closed) tab.close();
        toast(describeOnboardingError(err, 'The file could not be opened. Try again.'), { type: 'error' });
      } finally {
        setOpening(null);
      }
    },
    [toast]
  );

  return { opening, blocked, view };
}

/** The fallback link when the popup was blocked (18.27). */
export function EvidenceBlockedLink({ url }: { url: string }) {
  return (
    <p className="mt-2 text-xs text-gray-600 dark:text-gray-400" role="status">
      Your browser blocked the new tab.{' '}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary-700 underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
      >
        Open the file
      </a>
    </p>
  );
}

export default useEvidenceViewer;
